// dashboard/src/components/AdminGallery.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../api';

function AdminGallery() {
  // View state: 'albums' or 'media'
  const [view, setView] = useState('albums');
  const [selectedAlbum, setSelectedAlbum] = useState(null);

  // Album states
  const [albums, setAlbums] = useState([]);
  const [albumTitle, setAlbumTitle] = useState('');
  const [albumDescription, setAlbumDescription] = useState('');
  const [albumCoverFile, setAlbumCoverFile] = useState(null);
  const [albumSortOrder, setAlbumSortOrder] = useState(0);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);

  // Media states
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaCaption, setMediaCaption] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);

  // Auth
  const token = localStorage.getItem('token');
  const storedUser = localStorage.getItem('dashboardUser');
  const adminUser = storedUser ? JSON.parse(storedUser) : null;
  const userRole = adminUser?.role || '';
  const canManage = ['admin', 'master_admin', 'graphics'].includes(userRole);

  // ─── ALBUM FUNCTIONS ──────────────────────────────────────────────────────

  const fetchAlbums = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/gallery/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setAlbums(res.data.albums);
      }
    } catch (error) {
      console.error('Error fetching albums:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please log in again.');
      }
    }
  }, [token]);

  useEffect(() => {
    fetchAlbums();
  }, [fetchAlbums]);

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    if (!albumTitle.trim()) {
      alert('Album title is required');
      return;
    }
    setCreatingAlbum(true);
    try {
      const formData = new FormData();
      formData.append('title', albumTitle.trim());
      formData.append('description', albumDescription);
      formData.append('sortOrder', albumSortOrder);
      if (albumCoverFile) formData.append('coverImage', albumCoverFile);

      const res = await axios.post(`${API_BASE}/api/gallery`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) {
        alert('Album created successfully!');
        setAlbumTitle('');
        setAlbumDescription('');
        setAlbumCoverFile(null);
        setAlbumSortOrder(0);
        const fileInput = document.getElementById('albumCoverInput');
        if (fileInput) fileInput.value = '';
        fetchAlbums();
      }
    } catch (error) {
      console.error('Error creating album:', error);
      const msg = error.response?.data?.error || error.message;
      alert(`Failed to create album: ${msg}`);
    } finally {
      setCreatingAlbum(false);
    }
  };

  const handleUpdateAlbum = async () => {
    if (!editingAlbum) return;
    try {
      const formData = new FormData();
      formData.append('title', editingAlbum.title);
      formData.append('description', editingAlbum.description || '');
      formData.append('sortOrder', editingAlbum.sortOrder);
      formData.append('isActive', editingAlbum.isActive);
      if (editingAlbum.newCoverFile) formData.append('coverImage', editingAlbum.newCoverFile);

      const res = await axios.patch(`${API_BASE}/api/gallery/${editingAlbum._id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) {
        alert('Album updated successfully!');
        setEditingAlbum(null);
        fetchAlbums();
      }
    } catch (error) {
      console.error('Error updating album:', error);
      const msg = error.response?.data?.error || error.message;
      alert(`Failed to update album: ${msg}`);
    }
  };

  const handleDeleteAlbum = async (albumId, albumTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${albumTitle}" and ALL its media? This cannot be undone.`)) {
      return;
    }
    try {
      const res = await axios.delete(`${API_BASE}/api/gallery/${albumId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        alert('Album deleted successfully!');
        fetchAlbums();
      }
    } catch (error) {
      console.error('Error deleting album:', error);
      alert('Failed to delete album.');
    }
  };

  const handleToggleAlbumActive = async (album) => {
    try {
      const formData = new FormData();
      formData.append('isActive', !album.isActive);

      const res = await axios.patch(`${API_BASE}/api/gallery/${album._id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        alert(`Album ${!album.isActive ? 'activated' : 'deactivated'} successfully!`);
        fetchAlbums();
      }
    } catch (error) {
      console.error('Error toggling album:', error);
      alert('Failed to update album status.');
    }
  };

  const openMediaManager = (album) => {
    setSelectedAlbum(album);
    setView('media');
  };

  // ─── MEDIA FUNCTIONS ──────────────────────────────────────────────────────

  const fetchMedia = useCallback(async () => {
    if (!selectedAlbum) return;
    try {
      const res = await axios.get(`${API_BASE}/api/gallery/${selectedAlbum._id}/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setMediaItems(res.data.media);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
    }
  }, [selectedAlbum, token]);

  useEffect(() => {
    if (view === 'media' && selectedAlbum) {
      fetchMedia();
    }
  }, [view, selectedAlbum, fetchMedia]);

  const handleUploadMedia = async (e) => {
    e.preventDefault();
    if (!mediaFile) {
      alert('Please select a file to upload');
      return;
    }
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append('media', mediaFile);
      formData.append('caption', mediaCaption);

      const res = await axios.post(`${API_BASE}/api/gallery/${selectedAlbum._id}/media`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) {
        alert('Media uploaded successfully!');
        setMediaFile(null);
        setMediaCaption('');
        const fileInput = document.getElementById('mediaFileInput');
        if (fileInput) fileInput.value = '';
        fetchMedia();
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      if (error.response?.status === 413) {
        alert('File too large. Maximum size is 50MB.');
      } else {
        const msg = error.response?.data?.error || error.message;
        alert(`Failed to upload media: ${msg}`);
      }
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleDeleteMedia = async (mediaId, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) return;
    try {
      const res = await axios.delete(`${API_BASE}/api/gallery/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        alert('Media deleted successfully!');
        fetchMedia();
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Failed to delete media.');
    }
  };

  const handleToggleMediaActive = async (media) => {
    try {
      const res = await axios.patch(`${API_BASE}/api/gallery/media/${media._id}`,
        { isActive: !media.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        fetchMedia();
      }
    } catch (error) {
      console.error('Error toggling media:', error);
      alert('Failed to update media status.');
    }
  };

  const handleUpdateMediaCaption = async () => {
    if (!editingMedia) return;
    try {
      const res = await axios.patch(`${API_BASE}/api/gallery/media/${editingMedia._id}`,
        { caption: editingMedia.newCaption },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setEditingMedia(null);
        fetchMedia();
      }
    } catch (error) {
      console.error('Error updating caption:', error);
      alert('Failed to update caption.');
    }
  };

  // ─── ACCESS CHECK ─────────────────────────────────────────────────────────

  if (!canManage) {
    return (
      <div className="p-4">
        <h2 className="text-2xl font-bold mb-4">Gallery Management</h2>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong>Access Denied:</strong> You do not have permission to manage the gallery.
          Only master_admin, admin, and graphics roles can access this feature.
        </div>
      </div>
    );
  }

  // ─── MEDIA MANAGER VIEW ───────────────────────────────────────────────────

  if (view === 'media' && selectedAlbum) {
    const images = mediaItems.filter(m => m.mediaType === 'image');
    const videos = mediaItems.filter(m => m.mediaType === 'video');

    return (
      <div className="p-4">
        <button
          onClick={() => { setView('albums'); setSelectedAlbum(null); setMediaItems([]); }}
          className="mb-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          &larr; Back to Albums
        </button>

        <h2 className="text-2xl font-bold mb-2">
          {selectedAlbum.title} - Media Manager
        </h2>
        <p className="text-gray-600 mb-6">
          Upload and manage images and videos for this album. Images: {images.length}, Videos: {videos.length}
        </p>

        {/* Upload Form */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-4">Upload Media</h3>
          <form onSubmit={handleUploadMedia} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Image or Video
              </label>
              <input
                id="mediaFileInput"
                type="file"
                accept="image/*,video/*"
                onChange={(e) => setMediaFile(e.target.files[0] || null)}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Max file size: 50MB for videos, 10MB for images</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caption (Optional)
              </label>
              <input
                type="text"
                value={mediaCaption}
                onChange={(e) => setMediaCaption(e.target.value)}
                placeholder="Enter a caption..."
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={uploadingMedia || !mediaFile}
              className={`px-4 py-2 rounded-md text-white font-medium ${
                uploadingMedia || !mediaFile
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {uploadingMedia ? 'Uploading...' : 'Upload'}
            </button>
          </form>
        </div>

        {/* Images Grid */}
        {images.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <h3 className="text-lg font-semibold mb-4">Images ({images.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((item) => (
                <div key={item._id} className="border rounded-lg overflow-hidden shadow-sm">
                  <div className="relative">
                    <img
                      src={`${API_BASE}/${item.path}`}
                      alt={item.originalName}
                      className="w-full h-48 object-cover"
                    />
                    <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                      item.isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                    }`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-gray-500 truncate mb-1">{item.originalName}</p>
                    {editingMedia && editingMedia._id === item._id ? (
                      <div className="space-y-2 mb-2">
                        <input
                          type="text"
                          value={editingMedia.newCaption}
                          onChange={(e) => setEditingMedia({ ...editingMedia, newCaption: e.target.value })}
                          className="w-full p-1 text-xs border rounded"
                          placeholder="Caption..."
                        />
                        <div className="flex space-x-2">
                          <button onClick={handleUpdateMediaCaption} className="px-2 py-1 bg-green-500 text-white text-xs rounded">Save</button>
                          <button onClick={() => setEditingMedia(null)} className="px-2 py-1 bg-gray-500 text-white text-xs rounded">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-2">
                        <p className="text-xs text-gray-600">{item.caption || 'No caption'}</p>
                        <button onClick={() => setEditingMedia({ ...item, newCaption: item.caption || '' })} className="text-xs text-blue-600 hover:text-blue-800">Edit Caption</button>
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mb-2">
                      <p>By: {item.uploadedBy?.username || 'Unknown'}</p>
                      <p>{new Date(item.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleToggleMediaActive(item)}
                        className={`px-2 py-1 text-xs rounded font-medium ${
                          item.isActive ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {item.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteMedia(item._id, item.originalName)}
                        className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Videos Grid */}
        {videos.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <h3 className="text-lg font-semibold mb-4">Videos ({videos.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {videos.map((item) => (
                <div key={item._id} className="border rounded-lg overflow-hidden shadow-sm">
                  <div className="relative">
                    <video
                      src={`${API_BASE}/${item.path}`}
                      className="w-full h-48 object-cover"
                      controls
                    />
                    <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                      item.isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                    }`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-gray-500 truncate mb-1">{item.originalName}</p>
                    {editingMedia && editingMedia._id === item._id ? (
                      <div className="space-y-2 mb-2">
                        <input
                          type="text"
                          value={editingMedia.newCaption}
                          onChange={(e) => setEditingMedia({ ...editingMedia, newCaption: e.target.value })}
                          className="w-full p-1 text-xs border rounded"
                          placeholder="Caption..."
                        />
                        <div className="flex space-x-2">
                          <button onClick={handleUpdateMediaCaption} className="px-2 py-1 bg-green-500 text-white text-xs rounded">Save</button>
                          <button onClick={() => setEditingMedia(null)} className="px-2 py-1 bg-gray-500 text-white text-xs rounded">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-2">
                        <p className="text-xs text-gray-600">{item.caption || 'No caption'}</p>
                        <button onClick={() => setEditingMedia({ ...item, newCaption: item.caption || '' })} className="text-xs text-blue-600 hover:text-blue-800">Edit Caption</button>
                      </div>
                    )}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleToggleMediaActive(item)}
                        className={`px-2 py-1 text-xs rounded font-medium ${
                          item.isActive ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {item.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteMedia(item._id, item.originalName)}
                        className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {mediaItems.length === 0 && (
          <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">
            No media items in this album yet. Upload some above!
          </div>
        )}
      </div>
    );
  }

  // ─── ALBUM LIST VIEW ──────────────────────────────────────────────────────

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Gallery Management</h2>
      <p className="mb-6 text-gray-600">
        Create and manage gallery albums. Each album can contain images and videos that will be displayed on the website.
      </p>

      {/* Create Album Form */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-4">Create New Album</h3>
        <form onSubmit={handleCreateAlbum} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Album Title *</label>
              <input
                type="text"
                value={albumTitle}
                onChange={(e) => setAlbumTitle(e.target.value)}
                placeholder="e.g. Infrastructure"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort Order</label>
              <input
                type="number"
                value={albumSortOrder}
                onChange={(e) => setAlbumSortOrder(Number(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
            <textarea
              value={albumDescription}
              onChange={(e) => setAlbumDescription(e.target.value)}
              placeholder="Enter album description..."
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows="2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image (Optional)</label>
            <input
              id="albumCoverInput"
              type="file"
              accept="image/*"
              onChange={(e) => setAlbumCoverFile(e.target.files[0] || null)}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
          <button
            type="submit"
            disabled={creatingAlbum}
            className={`px-4 py-2 rounded-md text-white font-medium ${
              creatingAlbum ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {creatingAlbum ? 'Creating...' : 'Create Album'}
          </button>
        </form>
      </div>

      {/* Albums Grid */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Albums ({albums.length})</h3>

        {albums.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No albums created yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {albums.map((album) => (
              <div key={album._id} className="border rounded-lg overflow-hidden shadow-sm">
                {/* Cover Image */}
                <div className="relative h-40 bg-gray-200">
                  {album.coverImage ? (
                    <img
                      src={`${API_BASE}/${album.coverPath}`}
                      alt={album.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No Cover Image
                    </div>
                  )}
                  <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                    album.isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                  }`}>
                    {album.isActive ? 'Active' : 'Inactive'}
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-60 text-white text-xs rounded">
                    {album.mediaCount || 0} items
                  </div>
                </div>

                {/* Album Info */}
                <div className="p-4">
                  {editingAlbum && editingAlbum._id === album._id ? (
                    <div className="space-y-2 mb-3">
                      <input
                        type="text"
                        value={editingAlbum.title}
                        onChange={(e) => setEditingAlbum({ ...editingAlbum, title: e.target.value })}
                        className="w-full p-1 text-sm border rounded"
                        placeholder="Album title"
                      />
                      <textarea
                        value={editingAlbum.description || ''}
                        onChange={(e) => setEditingAlbum({ ...editingAlbum, description: e.target.value })}
                        className="w-full p-1 text-xs border rounded"
                        rows="2"
                        placeholder="Description"
                      />
                      <input
                        type="number"
                        value={editingAlbum.sortOrder}
                        onChange={(e) => setEditingAlbum({ ...editingAlbum, sortOrder: Number(e.target.value) })}
                        className="w-full p-1 text-xs border rounded"
                        placeholder="Sort order"
                      />
                      <div>
                        <label className="text-xs text-gray-600">Replace Cover:</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setEditingAlbum({ ...editingAlbum, newCoverFile: e.target.files[0] || null })}
                          className="block w-full text-xs mt-1"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <button onClick={handleUpdateAlbum} className="px-2 py-1 bg-green-500 text-white text-xs rounded">Save</button>
                        <button onClick={() => setEditingAlbum(null)} className="px-2 py-1 bg-gray-500 text-white text-xs rounded">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h4 className="font-semibold text-gray-900 mb-1">{album.title}</h4>
                      {album.description && (
                        <p className="text-xs text-gray-600 mb-2">{album.description}</p>
                      )}
                      <div className="text-xs text-gray-500 mb-3">
                        <p>Slug: /{album.slug}</p>
                        <p>Sort: {album.sortOrder}</p>
                        <p>Created: {new Date(album.createdAt).toLocaleDateString()}</p>
                        <p>By: {album.createdBy?.username || 'Unknown'}</p>
                      </div>
                    </>
                  )}

                  {!editingAlbum || editingAlbum._id !== album._id ? (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openMediaManager(album)}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded font-medium hover:bg-blue-700"
                      >
                        Manage Media
                      </button>
                      <button
                        onClick={() => setEditingAlbum({ ...album, newCoverFile: null })}
                        className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded font-medium hover:bg-gray-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleAlbumActive(album)}
                        className={`px-3 py-1 text-xs rounded font-medium ${
                          album.isActive ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {album.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteAlbum(album._id, album.title)}
                        className="px-3 py-1 bg-red-100 text-red-800 text-xs rounded font-medium hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminGallery;
